import cn from "classnames"
import React, { useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import { type Sign as ExampleSign } from "@/api/graphql"
import Checkbox from "@/common/components/checkbox"
import { useMinimaticData, useMinimaticLoaded } from "@/signomatic/app/minimatic"
import type Sign from "@/signomatic/app/sign"
import { type SignStateData } from "@/signomatic/app/sign"
import ColorUtil, { type ColorSet } from "@/signomatic/util/colors"
import { isText } from "@/signomatic/util/types"
import { ArrowLeftLarge, ArrowRightLarge, EngravingIcon, LockClosedFill } from "@/skylticons"

export interface MinimaticColorsConfiguration {
  backgroundColors?: string[]
  backgroundColorsEnabled?: boolean
  contentColors?: string[]
  contentColorsEnabled?: boolean
  fixedColors?: ColorSet[]
}

export type MinimaticColorsSign = Pick<
  ExampleSign,
  "dualcolor" | "transparent" | "multicolor" | "contentColor" | "backgroundColor"
>

interface MinimaticColorsProps {
  minimatic?: Sign
  loadingFailed?: boolean
  sign: MinimaticColorsSign
  config?: MinimaticColorsConfiguration | null
  setTextColor: (color: string) => void
  setColor: (color: string) => void
  setContentColor: (color: string) => void
  setMinimaticSlide: () => void
}

export const MinimaticColors: React.FC<MinimaticColorsProps> = ({
  minimatic,
  sign,
  config,
  loadingFailed,
  setTextColor,
  setColor,
  setContentColor,
  setMinimaticSlide,
}) => {
  const { t } = useTranslation()
  const loaded = useMinimaticLoaded(minimatic)

  if (minimatic && loaded && !minimatic.hasColorOptions) {
    return null
  }

  if (config && !loadingFailed && (!minimatic || !loaded)) {
    return (
      <div>
        {sign.multicolor && (
          <>
            {config.contentColorsEnabled && (
              <ColorPickerSkeleton label={t("misc.textfarg")} count={config.contentColors?.length} />
            )}
            {config.backgroundColorsEnabled && (
              <ColorPickerSkeleton label={t("misc.bakgrundsfarg")} count={config.backgroundColors?.length} />
            )}
          </>
        )}
        {sign.dualcolor && config.backgroundColorsEnabled && (
          <ColorPickerSkeleton label={t("toolbox.color")} count={config.fixedColors?.length} />
        )}
        {sign.transparent && config.contentColorsEnabled && (
          <ColorPickerSkeleton label={t("toolbox.color")} count={config.contentColors?.length} />
        )}
      </div>
    )
  }

  if (!minimatic || !loaded || !config) {
    return null
  }

  const props = {
    minimatic,
    sign,
    config,
    setTextColor,
    setColor,
    setContentColor,
    setMinimaticSlide,
  }

  return (
    <div className="minimatic-color-pickers">
      {sign.multicolor && (
        <>
          <ContentColorPicker {...props} />
          <BgColorPicker {...props} />
        </>
      )}
      {sign.dualcolor && <DualColorPicker {...props} />}
      {sign.transparent && <TransparentColorPicker {...props} />}
    </div>
  )
}

interface ColorPickerSkeletonProps {
  label: string
  count?: number
}

const ColorPickerSkeleton: React.FC<ColorPickerSkeletonProps> = ({ label, count }) => {
  if (!count || count === 0) {
    return null
  }

  return (
    <div className="mb-4">
      <p className="paragraph-xs mb-2">
        <span className="skeleton font-semibold text-ink-100">{label}:</span> <span className="skeleton">color</span>
      </p>
      <div className="flex flex-wrap gap-2">
        {Array.from(Array(count + 1)).map((_, i) => (
          <div
            key={i}
            className="relative flex h-10 w-10 items-center overflow-hidden rounded-full border border-transparent"
          >
            <div className="skeleton skeleton-rounded h-10 w-10"></div>
          </div>
        ))}
      </div>
    </div>
  )
}

interface ColorPickerTypeProps {
  minimatic: Sign
  sign: MinimaticColorsSign
  config: MinimaticColorsConfiguration
  setTextColor: (color: string) => void
  setColor: (color: string) => void
  setContentColor: (color: string) => void
  setMinimaticSlide: () => void
}

type handlerFunc<T extends ColorSet | string> = (
  minimatic: Sign
) => [handler: (data: SignStateData) => void, current: T | undefined, colors: T[]]

const useBgColorHandler: handlerFunc<string> = minimatic => {
  const [availableColors, setColors] = useState<string[]>([])
  const [current, setCurrent] = useState<string>()

  const handler = useCallback(
    ({ state, data }: SignStateData) => {
      setCurrent(state.color)

      if (["brass", "nickel"].includes(state.material)) {
        return
      }

      const currentData = data.find(data => data.name === minimatic.signType)

      if (currentData) {
        setColors(currentData.suggested_colors)
      }
    },
    [minimatic.signType]
  )

  return [handler, current, availableColors]
}

const BgColorPicker: React.FC<ColorPickerTypeProps> = ({ minimatic, sign, config, setColor, setMinimaticSlide }) => {
  const { t } = useTranslation()

  const [handler, current] = useBgColorHandler(minimatic)
  useMinimaticData(handler, minimatic)

  const onChange = (color: string) => {
    minimatic.updateState({ color })
    setMinimaticSlide()
    setColor(color)
  }

  if (
    !sign.backgroundColor ||
    !config.backgroundColorsEnabled ||
    !config.backgroundColors ||
    config.backgroundColors.length === 0
  ) {
    return null
  }

  return (
    <div className="mb-4">
      <p className="paragraph-xs mb-2">
        <span className="font-semibold text-ink-100">{t("misc.bakgrundsfarg")}:</span>{" "}
        {t(`background_colors.${current}`)}
      </p>
      <ColorPicker<string>
        colors={[sign.backgroundColor, ...config.backgroundColors]}
        active={[current || sign.backgroundColor]}
        onSelect={onChange}
      />
    </div>
  )
}

const useContentColorHandler: handlerFunc<string> = minimatic => {
  const [availableColors, setColors] = useState<string[]>([])
  const [current, setCurrent] = useState<string>()

  const handler = useCallback(
    ({ state, data }: SignStateData) => {
      const textObj = state.objects.find(isText)
      setCurrent(textObj?.color)

      const currentData = data.find(data => data.name === minimatic.signType)

      if (currentData) {
        setColors(ColorUtil.filterSuggestedContentColors(currentData.suggested_colors, currentData))
      }
    },
    [minimatic.signType]
  )

  return [handler, current, availableColors]
}

const ContentColorPicker: React.FC<ColorPickerTypeProps> = ({
  minimatic,
  sign,
  config,
  setTextColor,
  setMinimaticSlide,
}) => {
  const { t } = useTranslation()

  const [handler, current] = useContentColorHandler(minimatic)
  useMinimaticData(handler, minimatic)

  const onChange = (color: string) => {
    minimatic.updateTexts({ color })
    minimatic.updateImages({ color })

    setMinimaticSlide()
    setTextColor(color)
  }

  if (
    !sign.contentColor ||
    !config.contentColorsEnabled ||
    !config.contentColors ||
    config.contentColors.length === 0
  ) {
    return null
  }

  return (
    <div className="mb-4">
      <p className="paragraph-xs mb-2">
        <span className="font-semibold text-ink-100">{t("misc.textfarg")}:</span> {t(`content_colors.${current}`)}
      </p>
      <ColorPicker<string>
        colors={[sign.contentColor, ...config.contentColors]}
        active={[current || sign.contentColor]}
        onSelect={onChange}
      />
    </div>
  )
}

const useDualColorHandler: handlerFunc<ColorSet> = minimatic => {
  const [availableColors, setColors] = useState<ColorSet[]>([])
  const [current, setCurrent] = useState<ColorSet>()

  const handler = useCallback(
    ({ state, data }: SignStateData) => {
      setCurrent([state.color, state.contentColor || ""])

      const currentData = data.find(data => data.name === minimatic.signType)

      if (currentData) {
        setColors(ColorUtil.filterColorSets(currentData.fixed_colors as ColorSet[], currentData, state))
      }
    },
    [minimatic.signType]
  )

  return [handler, current, availableColors]
}

const DualColorPicker: React.FC<ColorPickerTypeProps> = ({
  minimatic,
  sign,
  config,
  setColor,
  setContentColor,
  setMinimaticSlide,
}) => {
  const { t } = useTranslation()

  const [handler, current] = useDualColorHandler(minimatic)
  useMinimaticData(handler, minimatic)

  const onChange = (set: ColorSet) => {
    minimatic.updateState({ color: set[0], contentColor: set[1] })
    setMinimaticSlide()
    setColor(set[0])
    setContentColor(set[1])
  }

  if (
    !sign.backgroundColor ||
    !sign.contentColor ||
    !config.backgroundColorsEnabled ||
    !config.fixedColors ||
    config.fixedColors.length === 0
  ) {
    return null
  }

  return (
    <div className="mb-4">
      <p className="paragraph-xs mb-2">
        <span className="font-semibold text-ink-100">{t("toolbox.color")}:</span>{" "}
        {current && current[0] && t(`background_colors.${current[0]}`)}
        {current && current[1] && `/${t(`content_colors.${current[1]}`)}`}
      </p>
      <ColorPicker<ColorSet>
        colors={[[sign.backgroundColor, sign.contentColor], ...config.fixedColors]}
        active={[current || [sign.backgroundColor, sign.contentColor]]}
        onSelect={onChange}
      />
    </div>
  )
}

const useTransparentColorHandler: handlerFunc<string> = minimatic => {
  const [availableColors, setColors] = useState<string[]>([])
  const [current, setCurrent] = useState<string>()

  const handler = useCallback(
    ({ state, data }: SignStateData) => {
      setCurrent(state.contentColor)

      const currentData = data.find(data => data.name === minimatic.signType)

      if (currentData) {
        setColors(currentData.fixed_colors as string[])
      }
    },
    [minimatic.signType]
  )

  return [handler, current, availableColors]
}

const TransparentColorPicker: React.FC<ColorPickerTypeProps> = ({
  minimatic,
  sign,
  config,
  setContentColor,
  setMinimaticSlide,
}) => {
  const { t } = useTranslation()

  const [handler, current] = useTransparentColorHandler(minimatic)
  useMinimaticData(handler, minimatic)

  const onChange = (contentColor: string) => {
    minimatic.updateState({ contentColor })
    setMinimaticSlide()
    setContentColor(contentColor)
  }

  if (
    !sign.contentColor ||
    !config.contentColorsEnabled ||
    !config.contentColors ||
    config.contentColors.length === 0
  ) {
    return null
  }

  return (
    <div className="mb-4">
      <p className="paragraph-xs mb-2">
        <span className="font-semibold text-ink-100">{t("toolbox.color")}:</span> {t(`background_colors.${current}`)}
      </p>
      <ColorPicker<string>
        colors={[sign.contentColor, ...config.contentColors]}
        active={[current || sign.contentColor]}
        onSelect={onChange}
      />
    </div>
  )
}

interface MinimaticColorsConfigurerProps {
  minimatic: Sign
  sign?: MinimaticColorsSign | null
  config?: MinimaticColorsConfiguration
  update: (config: Partial<MinimaticColorsConfiguration>) => void
}

export const MinimaticColorsConfigurer: React.FC<MinimaticColorsConfigurerProps> = ({
  minimatic,
  sign,
  config,
  update,
}) => {
  const loaded = useMinimaticLoaded(minimatic)

  if (minimatic && loaded && !minimatic.hasColorOptions) {
    return null
  }

  if (!loaded) {
    return null
  }

  return (
    <>
      {sign?.multicolor && (
        <>
          <ContentColorPickerConfig minimatic={minimatic} update={update} config={config} />
          <BgColorPickerConfig minimatic={minimatic} update={update} config={config} />
        </>
      )}
      {sign?.dualcolor && <DualColorPickerConfig minimatic={minimatic} update={update} config={config} />}
      {sign?.transparent && <TransparentColorPickerConfig minimatic={minimatic} update={update} config={config} />}
    </>
  )
}

const BgColorPickerConfig: React.FC<MinimaticColorsConfigurerProps> = ({ minimatic, config, update }) => {
  const [handler, current, availableColors] = useBgColorHandler(minimatic)
  useMinimaticData(handler, minimatic)

  if (!current) {
    return null
  }

  return (
    <ColorPickerConfigurer<string>
      title="background colors"
      current={current}
      availableColors={availableColors}
      activeColors={config?.backgroundColors || []}
      setActiveColors={c => update({ backgroundColors: c })}
      active={typeof config?.backgroundColorsEnabled !== "undefined" ? config.backgroundColorsEnabled : true}
      setActive={a => update({ backgroundColorsEnabled: a })}
    />
  )
}

const ContentColorPickerConfig: React.FC<MinimaticColorsConfigurerProps> = ({ minimatic, config, update }) => {
  const [handler, current, availableColors] = useContentColorHandler(minimatic)
  useMinimaticData(handler, minimatic)

  if (!current) {
    return null
  }

  return (
    <ColorPickerConfigurer<string>
      title="text colors"
      current={current}
      availableColors={availableColors}
      activeColors={config?.contentColors || []}
      setActiveColors={c => update({ contentColors: c })}
      active={typeof config?.contentColorsEnabled !== "undefined" ? config.contentColorsEnabled : true}
      setActive={a => update({ contentColorsEnabled: a })}
    />
  )
}

const DualColorPickerConfig: React.FC<MinimaticColorsConfigurerProps> = ({ minimatic, config, update }) => {
  const [handler, current, availableColors] = useDualColorHandler(minimatic)
  useMinimaticData(handler, minimatic)

  if (!current) {
    return null
  }

  return (
    <ColorPickerConfigurer<ColorSet>
      title="colors"
      current={current}
      availableColors={availableColors}
      activeColors={config?.fixedColors || []}
      setActiveColors={c => update({ fixedColors: c })}
      active={typeof config?.backgroundColorsEnabled !== "undefined" ? config.backgroundColorsEnabled : true}
      setActive={a => update({ backgroundColorsEnabled: a })}
    />
  )
}

const TransparentColorPickerConfig: React.FC<MinimaticColorsConfigurerProps> = ({ minimatic, config, update }) => {
  const [handler, current, availableColors] = useTransparentColorHandler(minimatic)
  useMinimaticData(handler, minimatic)

  if (!current) {
    return null
  }

  return (
    <ColorPickerConfigurer<string>
      title="colors"
      current={current}
      availableColors={availableColors}
      activeColors={config?.contentColors || []}
      setActiveColors={c => update({ contentColors: c })}
      active={typeof config?.contentColorsEnabled !== "undefined" ? config.contentColorsEnabled : true}
      setActive={a => update({ contentColorsEnabled: a })}
    />
  )
}

interface ColorPickerConfigurerProps<T> {
  title: string
  availableColors: T[]
  activeColors: T[]
  setActiveColors: (colors: T[]) => void
  current: T
  active: boolean
  setActive: (active: boolean) => void
}

const ColorPickerConfigurer = <T extends ColorSet | string>({
  title,
  availableColors,
  current,
  activeColors,
  setActiveColors,
  active,
  setActive,
}: ColorPickerConfigurerProps<T>) => {
  const [availableColorsSelected, setAvailableColorsSelected] = useState<T[]>([])
  const [activeColorsSelected, setActiveColorsSelected] = useState<T[]>([])

  const toggleColor = (set: T) => {
    setAvailableColorsSelected(selected => {
      if (selected.find(c => ColorUtil.sameColor(c, set))) {
        return selected.filter(c => !ColorUtil.sameColor(c, set))
      } else {
        return [...selected, set]
      }
    })
  }

  const toggleActiveColor = (set: T) => {
    setActiveColorsSelected(selected => {
      if (selected.find(c => ColorUtil.sameColor(c, set))) {
        return selected.filter(c => !ColorUtil.sameColor(c, set))
      } else {
        return [...selected, set]
      }
    })
  }

  const moveToAvailable = () => {
    setActiveColorsSelected([])
    setActiveColors(activeColors.filter(c => !activeColorsSelected.find(a => ColorUtil.sameColor(a, c))))
  }

  const moveToActive = () => {
    setAvailableColorsSelected([])
    setActiveColors([...activeColors, ...availableColorsSelected])
  }

  return (
    <div className="minimatic-colors-config grid grid-cols-[1fr_min-content_1fr]" data-title={title}>
      <div className="mb-3 flex items-center gap-2">
        <h4 className="mb-0 w-auto whitespace-nowrap text-xl leading-6">All {title}</h4>
        <p className="paragraph-sm mb-0 leading-6">Aim for 2-8 colors</p>
      </div>
      <div></div>
      <div className="mb-3 flex items-center gap-3 [&_.checkbox-label]:!mb-0 [&_.checkbox]:mb-0 [&_.checkbox]:py-0">
        <h4 className="mb-0 w-auto whitespace-nowrap text-xl leading-6">Active {title}</h4>
        <Checkbox name="toggle_colors" checked={active} label="Activate" onChange={e => setActive(e.target.checked)} />
      </div>
      <div className="minimatic-colors-available rounded-lg border border-white-20 p-4">
        <ColorPicker<T>
          colors={availableColors.filter(
            c => !activeColors.find(a => ColorUtil.sameColor(a, c)) && !ColorUtil.sameColor(c, current)
          )}
          active={availableColorsSelected}
          onSelect={toggleColor}
        />
      </div>
      <div className="flex items-center">
        <div className="space-y-2 p-1">
          <div>
            <button
              className="minimatic-activate-colors button button-secondary icon-only mb-0"
              type="button"
              disabled={availableColorsSelected.length === 0}
              onClick={moveToActive}
            >
              <ArrowRightLarge className="h-6 w-6" />
            </button>
          </div>
          <div>
            <button
              className="minimatic-deactivate-colors button button-secondary icon-only mb-0"
              type="button"
              disabled={activeColorsSelected.length === 0}
              onClick={moveToAvailable}
            >
              <ArrowLeftLarge className="h-6 w-6" />
            </button>
          </div>
        </div>
      </div>
      <div className="minimatic-colors-selected rounded-lg border border-white-20 p-4">
        <ColorPicker<T>
          colors={[current, ...activeColors]}
          active={activeColorsSelected}
          locked={[current]}
          onSelect={toggleActiveColor}
        />
      </div>
    </div>
  )
}

interface ColorPickerProps<T> {
  colors: T[]
  active?: Array<T | undefined>
  locked?: Array<T | undefined>
  onSelect: (colors: T) => void
}

const ColorPicker = <T extends ColorSet | string>({ colors, active, locked, onSelect }: ColorPickerProps<T>) => {
  return (
    <div className="flex flex-wrap gap-2">
      {colors.map(set => {
        const lockedColor = locked?.find(c => ColorUtil.sameColor(set, c))

        return (
          <div
            key={set.toString()}
            className={cn(
              "minimatic-color relative flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border transition hover:border-ink-100 hover:opacity-90",
              ColorUtil.colorClass(set),
              `color-${ColorUtil.colorClass(set)}`,
              {
                "ring-2 ring-primary ring-offset-2": active?.find(c => ColorUtil.sameColor(set, c)),
                "border-transparent": ColorUtil.backgroundColor(set) !== "#ffffff",
                "pointer-events-none opacity-90": lockedColor,
              }
            )}
            style={{
              backgroundColor: ColorUtil.backgroundColor(set),
              backgroundImage: ColorUtil.backgroundImage(set),
            }}
            onClick={() => onSelect(set)}
          >
            {ColorUtil.isSet(set) && (
              <div className="h-6 w-6" style={{ color: ColorUtil.textColor(set) }}>
                <EngravingIcon />
              </div>
            )}
            {lockedColor && (
              <div className="absolute -translate-x-3/4 translate-y-1/3">
                <LockClosedFill className="h-6 w-6 [&_path]:stroke-white-3 [&_path]:stroke-1" />
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
